import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ReactComponent as CloseIcon } from '../../../shared/icons/close-icon.svg';
import { useIntl } from 'react-intl';
import '../../../components/document-wizard/document-wizard-header/document-wizard-header.scss';
import { TemplateBadgeContainer } from './TemplateBadgeContainer';

import { TemplateViewDropdown } from '../../../components/document-wizard/document-wizard-header/document-revision-swithcer/TemplateViewDropdown';
import { DocumentTemplateType } from '../../../shared/templateType';
import { Button } from 'react-bootstrap';
import { TemplateType } from '../../../consts/forms-types';
import CreateExpressTemplateDocument from '../../deals/document-template-modal/CreateExpressTemplateDocument';
import { PublishChangesButton } from '../../../components/templates/PublishChangesButton';
import { ActiveInactiveDropdown } from '../../../components/templates/ActiveInactiveDropdown';
import { DocumentWizardHeaderLastSaved } from '../../../components/document-wizard/document-wizard-header/document-wizard-header-lastsaved/DocumentWizardHeaderLastSaved';
import { Tooltip } from 'react-tooltip';

export const KEY_ENTER_CODE = 13;

export type TemplateEditorHeaderProps = {
  title: string;
  templateTitle: string;
  templateId: string;
  teamId: string;
  canEdit: boolean;
  isUploaded: boolean;
  teamEditPermissions: boolean;
  onSaveTitle: (value: string) => void;
  onClose: () => void;
  templateType: string | DocumentTemplateType;
  showCloseButton?: boolean;
  isRestrictedEditingEnabled?: boolean;
  isOrganizationTemplate?: boolean;
  isSharedFromWorkspace?: boolean;
  // Template versioning props
  hasPendingChanges?: boolean;
  hasPendingSuggestions?: boolean;
  canPublish?: boolean;
  canManageTemplates?: boolean;
  onPublishSuccess?: () => void;
  activeTab?: 'edit' | 'compare';
  onTabChange?: (tab: 'edit' | 'compare') => void;
  onViewPublished?: () => void; // New prop for published slideout
  // Active/inactive toggle props
  isTemplateActive?: boolean;
  onStatusChange?: () => void;
  // Last saved timestamp
  lastUpdated?: string;
  // Whether the template has been published at least once
  isPublished?: boolean;
  // V4.0: DOCX-native editor — tightens the header to read as one google-docs
  // style bar with the editor's File menu directly below (no divider).
  isDocxNative?: boolean;
  // DOCX-native: true when the in-place read-only Published view is active
  // (drives the dropdown label to show "Published").
  isViewingPublished?: boolean;
  // DOCX-native: true when the in-place Compare view is active.
  isViewingCompare?: boolean;
};

export const TemplatePageHeader = ({
  onClose,
  title,
  canEdit,
  onSaveTitle,
  isUploaded,
  templateType,
  templateTitle,
  teamId,
  templateId,
  teamEditPermissions,
  showCloseButton = false,
  isRestrictedEditingEnabled = false,
  isOrganizationTemplate = false,
  isSharedFromWorkspace = false,
  hasPendingChanges = false,
  hasPendingSuggestions = false,
  canPublish = false,
  canManageTemplates = false,
  onPublishSuccess,
  activeTab,
  onTabChange,
  onViewPublished,
  isTemplateActive = false,
  onStatusChange,
  lastUpdated,
  isPublished = false,
  isDocxNative = false,
  isViewingPublished = false,
  isViewingCompare = false,
}: TemplateEditorHeaderProps) => {
  const intl = useIntl();
  const inputId = 'document-wizard-title-editor';
  const [titleValue, setTitleValue] = useState(title);
  const [openUseTemplateModal, setUseTemplateModal] = useState(false);
  const [previousValue, setPreviousValue] = useState(titleValue);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCloseClick = () => {
    onClose();
  };

  const handleSave = useCallback(() => {
    if (!titleValue) {
      setTitleValue(previousValue);
      return;
    }
    onSaveTitle(titleValue);
  }, [onSaveTitle, titleValue, previousValue]);

  const handleChange = useCallback(
    (evt) => {
      setPreviousValue(titleValue);
      setTitleValue(evt.target.value);
    },
    [titleValue]
  );

  const handleKeyDown = useCallback((evt) => {
    // on hit enter
    if (evt.keyCode === KEY_ENTER_CODE) {
      evt.preventDefault();
      setTitleValue(evt.target.value);
      inputRef.current?.blur();
    }
  }, []);

  const labelTitle = canEdit
    ? intl.formatMessage({ id: 'document-wizard.header.hover.title', defaultMessage: 'Rename title' })
    : '';

  useEffect(() => {
    setTitleValue(title);
  }, [title]);

  const canUseTemplate = isPublished && isTemplateActive;
  const useTemplateDisabledReason = !isPublished
    ? 'Template must be published before it can be used'
    : !isTemplateActive
      ? 'Template is inactive'
      : undefined;

  const useTemplateButton = (
    <Button
      variant="primary"
      className="use-template-btn"
      onClick={() => setUseTemplateModal(true)}
      disabled={!canUseTemplate}
    >
      Use Template
    </Button>
  );

  const useTemplateTooltipId = `use-template-tooltip-${templateId}`;

  const displayUseTemplateButton = templateType !== TemplateType.CLAUSE && teamEditPermissions && (
    !canUseTemplate ? (
      <>
        <span id={useTemplateTooltipId} className="d-inline-block" style={{ cursor: 'not-allowed' }}>
          {useTemplateButton}
        </span>
        <Tooltip
          anchorSelect={`#${useTemplateTooltipId}`}
          place="bottom"
          style={{
            maxWidth: '220px',
            zIndex: 1060,
            fontSize: '13px',
            padding: '8px 12px',
            borderRadius: '4px',
            textAlign: 'center',
          }}
          opacity={1}
        >
          {useTemplateDisabledReason}
        </Tooltip>
      </>
    ) : useTemplateButton
  );

  return (
    <header
      className={classNames('document-wizard-header is-template', {
        'is-docx-native': isDocxNative,
      })}
    >
      {/* Single Row Layout */}
      <div
        className={classNames('document-wizard-header__single-row', {
          'has-restricted-close': showCloseButton || isOrganizationTemplate || isSharedFromWorkspace || !canEdit,
        })}
      >
        {(showCloseButton || isOrganizationTemplate || isSharedFromWorkspace || !canEdit) && (
          <button
            type="button"
            onClick={handleCloseClick}
            className="document-wizard-header__close-button"
            data-testid="template-header__close-cta"
            title="Close"
          >
            <CloseIcon className="document-wizard-header__close-icon" />
          </button>
        )}

        {/* Left side: View dropdown + template name + type pill */}
        <div className="document-wizard-header__left-group">
          {/* View dropdown for editing users, or readonly for org members */}
          {!isUploaded && canEdit && canManageTemplates ? (
            <TemplateViewDropdown
              activeTab={activeTab}
              onTabChange={onTabChange}
              onViewPublished={onViewPublished}
              canManageTemplates={canManageTemplates}
              hasPendingChanges={hasPendingChanges}
              isViewingPublished={isViewingPublished}
              isViewingCompare={isViewingCompare}
              isDocxNative={isDocxNative}
            />
          ) : null}

          {/* Template name field */}
          <div className="document-wizard-header-title document-wizard-header-title--single-row">
            {canEdit && (
              <input
                ref={inputRef}
                type="text"
                id={inputId}
                value={titleValue}
                onChange={handleChange}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className={classNames('document-wizard-header-title__input form-control')}
              />
            )}

            <label
              htmlFor={inputId}
              className={classNames(
                'document-wizard-header-title__label document-wizard-header-title__label--truncate',
                {
                  'document-wizard-header-title__label--can-edit': canEdit,
                }
              )}
              title={labelTitle || titleValue}
            >
              {titleValue}
            </label>
          </div>

          {/* Template type pill */}
          <TemplateBadgeContainer templateType={templateType} />
        </div>

        {/* Right side: Last Saved + buttons */}
        <div className="document-wizard-header__right-group">
          {lastUpdated && (
            <div className="document-wizard-header__last-updated small">
              Last Saved: <DocumentWizardHeaderLastSaved datetime={lastUpdated} intervalInSecond={60} />
            </div>
          )}
          {canEdit && canManageTemplates && (
            <ActiveInactiveDropdown
              templateId={templateId}
              teamId={teamId}
              isActive={isTemplateActive}
              templateType={templateType as TemplateType}
              canManageTemplates={canManageTemplates}
              onStatusChange={onStatusChange}
            />
          )}
          {displayUseTemplateButton}
          {!isUploaded && canEdit && canManageTemplates && (
            <PublishChangesButton
              templateId={templateId}
              templateName={templateTitle}
              templateType={templateType as TemplateType}
              hasPendingChanges={hasPendingChanges}
              hasPendingSuggestions={hasPendingSuggestions}
              canPublish={canPublish}
              canManageTemplates={canManageTemplates}
              onPublishSuccess={onPublishSuccess}
              isDocxNative={isDocxNative}
            />
          )}
        </div>
      </div>

      <CreateExpressTemplateDocument
        isOpen={openUseTemplateModal}
        onClose={() => setUseTemplateModal(false)}
        templateName={templateTitle}
        teamId={teamId}
        templateId={templateId}
      />
    </header>
  );
};
